// THE route table. One definition, five readers.
//
// Before this file existed, a new page had to be added by hand in four places:
// App.jsx (the runtime router), entry-server.jsx (the SSR route table),
// scripts/prerender.js (the URL list) and scripts/generate-sitemap.js. They had
// already drifted: on 2026-10-02 the new `/` was live in App.jsx while the
// prerender was still baking the OLD homepage into dist/index.html, which means
// every crawler and every link unfurler would have been served the wrong page.
//
// A route missing from the prerender ships an empty <div id="root"> to Googlebot.
// That is the worst SEO failure available to this codebase, and it is silent. So:
// add a row HERE and nowhere else.
//
//   path        the URL
//   page        the module basename under src/pages. THE single mention of the
//               page file anywhere in the repo: the client's code split chunk,
//               the SSR eager render and prerender.js's modulepreload hint are
//               all derived from this one string (see "One name, three uses")
//   prerender   bake a static .html at build time. Everything public should
//   out         where that file lands, relative to dist/
//   sitemap     { priority, freq } to list it, or null to leave it out
//   noindex     documentation only — the PAGE renders its own <Seo noindex>.
//               Nothing reads this field; it is here so the table tells the
//               truth about which URLs are deliberately out of the index
//
// ─────────────────────────────────────────────────────────────────────────────
// One name, three uses: why pages are named, not imported
// ─────────────────────────────────────────────────────────────────────────────
// This file used to `import FindWork from './pages/FindWork'` at the top, which
// put EVERY page in the one first-load bundle. That is how 105 KB of legal prose
// (practiceTerms + workerPrivacy + practicePrivacy + practiceCookies, all
// statically imported by LegalDocument.jsx) came to be downloaded by someone
// reading the homepage, and it is most of why first-load JS was 150 KB gzipped
// against a 120 KB budget (docs/MARKETING-SITE-SPEC.md §7.3).
//
// So rows name their page instead of importing it, and the two consumers that
// need an actual component derive one in the way that suits them:
//
//   · THE CLIENT (App.jsx) reads `ROUTES`, where `Component` is a code split
//     gate: one chunk per page, fetched when that route is first rendered.
//
//   · THE SSR PRERENDER (entry-server.jsx) reads `withEagerComponents(...)`,
//     where `Component` is the real module, imported eagerly.
//
// ⚠️ The SSR path CANNOT be lazy, and this is the constraint the whole design
// turns on. `renderToString` is synchronous and has no way to wait: a suspended
// tree renders as its Suspense fallback, so every prerendered page would bake an
// empty placeholder into dist/*.html — the exact empty-shell-to-Googlebot
// failure the table exists to prevent, except harder to notice because the build
// would still pass. entry-server.jsx therefore resolves the page modules
// synchronously with `import.meta.glob(..., { eager: true })` and hands them
// here. The glob lives THERE rather than in this file on purpose: eager glob
// imports compile to plain static imports, so a glob in this module would be in
// the client's graph and would silently undo the entire split.

import { lazy } from 'react'

// Vite rewrites a template-literal dynamic import into a switch over a glob of
// `./pages/*.jsx`, giving every page its own chunk while the specifier is still
// written once, derived from the row's `page`. Keep the literal shape — build an
// import specifier by string concatenation instead and Vite can no longer see
// which files are reachable, so it bundles nothing and the route 404s at runtime.
const loadPage = (page) => () => import(`./pages/${page}.jsx`)

// A route's client-side component.
//
// Two render paths, because the initial route and a later navigation have
// genuinely different requirements:
//
//   · NAVIGATION (someone clicks a link) → React.lazy. The current page is
//     already on screen, so suspending is cheap and the Suspense boundary in
//     App.jsx covers it.
//
//   · FIRST PAINT → the resolved module, rendered synchronously, no suspend.
//     This matters because main.jsx mounts with createRoot(), NOT hydrateRoot():
//     the client throws the prerendered markup away and renders from scratch. If
//     the first render suspended, React would commit the Suspense fallback,
//     which CLEARS the container — a page that had already painted its hero from
//     static HTML would go blank and then come back. So main.jsx awaits
//     `Component.preload()` for the route being visited before it mounts, which
//     sets `Resolved` and makes that first render synchronous.
//
// Switching from <Lazy> to <Resolved> would remount the subtree, so preload() is
// only ever called before the first render. It is not a general purpose prefetch.
function routeComponent(page) {
  const load = loadPage(page)
  const Lazy = lazy(load)
  let Resolved = null

  const Gate = (props) => (Resolved ? <Resolved {...props} /> : <Lazy {...props} />)
  Gate.displayName = `Route(${page})`
  Gate.preload = () => load().then((m) => { Resolved = m.default })
  return Gate
}

// The table. Plain data: no component, no import, nothing a node script cannot
// read straight out of the compiled SSR bundle.
const ROUTE_TABLE = [
  // ── Find work trunk ──────────────────────────────────────────────────────
  { path: '/', page: 'FindWork', prerender: true, out: 'index.html',
    sitemap: { priority: 1.0, freq: 'weekly' } },
  { path: '/how-it-works', page: 'HowItWorks', prerender: true,
    out: 'how-it-works/index.html', sitemap: { priority: 0.8, freq: 'monthly' } },
  { path: '/getting-paid', page: 'GettingPaid', prerender: true,
    out: 'getting-paid/index.html', sitemap: { priority: 0.9, freq: 'monthly' } },
  { path: '/trust', page: 'Trust', prerender: true,
    out: 'trust/index.html', sitemap: { priority: 0.7, freq: 'monthly' } },

  // ── Get cover trunk ──────────────────────────────────────────────────────
  { path: '/business', page: 'GetCover', prerender: true,
    out: 'business/index.html', sitemap: { priority: 0.9, freq: 'weekly' } },
  { path: '/business/how-it-works', page: 'CoverHowItWorks', prerender: true,
    out: 'business/how-it-works/index.html', sitemap: { priority: 0.8, freq: 'monthly' } },
  { path: '/business/pricing', page: 'CoverPricing', prerender: true,
    out: 'business/pricing/index.html', sitemap: { priority: 0.9, freq: 'monthly' } },
  { path: '/business/compliance', page: 'CoverCompliance', prerender: true,
    out: 'business/compliance/index.html', sitemap: { priority: 0.8, freq: 'monthly' } },

  // ── Evergreen ────────────────────────────────────────────────────────────
  { path: '/signup', page: 'SignUp', prerender: true,
    out: 'signup/index.html', sitemap: { priority: 0.9, freq: 'weekly' } },
  // Where sign up lands. Prerendered so it paints instantly after the post, but
  // noindex and out of the sitemap: it means nothing without the form before it.
  { path: '/welcome', page: 'Welcome', prerender: true,
    out: 'welcome/index.html', sitemap: null, noindex: true },
  { path: '/about', page: 'About', prerender: true,
    out: 'about/index.html', sitemap: { priority: 0.5, freq: 'yearly' } },
  { path: '/faq', page: 'Faq', prerender: true,
    out: 'faq/index.html', sitemap: { priority: 0.6, freq: 'monthly' } },
  { path: '/legal', page: 'LegalIndex', prerender: true,
    out: 'legal/index.html', sitemap: { priority: 0.3, freq: 'yearly' } },
  // A dynamic route cannot be prerendered from its pattern, so the concrete
  // slugs are listed. They MUST be: without them try_files falls through to
  // /index.html and a crawler reading /legal/terms is served the HOMEPAGE's
  // bytes, with the terms only appearing after the SPA boots. Legal documents
  // are linked from both apps, the sign up consent lines and Google's OAuth
  // consent screen, so they have to be real HTML.
  //
  // This is also the page that pays for the code split: it statically imports
  // all four legal documents, ~105 KB of raw prose, which now lives in its own
  // chunk and is downloaded only by someone actually reading a legal document.
  // Four documents, four pages, four chunks. ⚠️ This was ONE page on a :slug,
  // which meant the component statically imported all four documents and a
  // visitor reading the Cookie Notice downloaded the Terms of Business too:
  // ~105 KB of prose in a single chunk, and the whole of the first-load
  // overage. Splitting them is why the legal routes now cost what they read.
  //
  // They are listed individually rather than generated because each one must
  // also be prerendered: a dynamic route cannot be, and without a real file
  // try_files falls through to /index.html and serves a crawler the HOMEPAGE's
  // bytes. These pages are linked from both apps, the sign up consent lines and
  // Google's OAuth consent screen.
  { path: '/legal/terms', page: 'LegalTerms', prerender: true,
    out: 'legal/terms/index.html', sitemap: { priority: 0.3, freq: 'yearly' } },
  { path: '/legal/privacy', page: 'LegalPrivacy', prerender: true,
    out: 'legal/privacy/index.html', sitemap: { priority: 0.3, freq: 'yearly' } },
  { path: '/legal/cookies', page: 'LegalCookies', prerender: true,
    out: 'legal/cookies/index.html', sitemap: { priority: 0.2, freq: 'yearly' } },
  { path: '/legal/worker-privacy', page: 'LegalWorkerPrivacy', prerender: true,
    out: 'legal/worker-privacy/index.html', sitemap: { priority: 0.2, freq: 'yearly' } },
  // Prerendered even though its data is live. Without it, try_files falls through
  // to /index.html and a direct hit, or a crawler, is served the HOMEPAGE's bytes
  // with the status page only appearing once the SPA boots. Both apps open this
  // URL from their "trouble connecting" screens, which is exactly the moment the
  // SPA may be the thing that is struggling. The prerendered copy carries the
  // page's own loading state, then it refreshes itself from the feed.
  { path: '/status', page: 'ServiceStatus', prerender: true,
    out: 'status/index.html', sitemap: null },

  // ── Utility: noindex, and deliberately absent from the sitemap ───────────
  { path: '/get', page: 'GetApp', prerender: true, out: 'get/index.html',
    sitemap: null, noindex: true },
  { path: '/continue', page: 'ContinueInApp', prerender: true,
    out: 'continue/index.html', sitemap: null, noindex: true },

  // The 404. Prerendered so Caddy can serve it as the error page body, noindex
  // so the URLs that land on it never enter the index. App.jsx also renders this
  // page for its catch-all route, and main.jsx preloads it when the URL matches
  // nothing — Caddy's try_files serves /index.html for an unknown path, so a bad
  // deep link arrives with the homepage's bytes and the SPA has to resolve it.
  { path: '/404', page: 'NotFound', prerender: true, out: '404.html',
    sitemap: null, noindex: true },

  // ⚠️ /join and /join/* are NOT here and must never be. The referral landing is
  // server rendered by the API so the share preview can carry per-code Open
  // Graph tags, and Caddy reverse-proxies it. A file at dist/join/index.html
  // would be matched by try_files FIRST and would silently shadow the real
  // landing. See the Caddyfile.
]

// The client's view of the table: every page behind its own chunk.
export const ROUTES = ROUTE_TABLE.map((route) => ({
  ...route,
  Component: routeComponent(route.page),
}))

// The SSR prerender's view of the table: the real page components, resolved
// synchronously from the eager glob entry-server.jsx passes in.
//
// `pageModules` is the result of `import.meta.glob('./pages/*.jsx', { eager:
// true })` — keys are specifiers relative to src/, values are the modules.
export function withEagerComponents(pageModules) {
  return ROUTE_TABLE.map((route) => {
    const key = `./pages/${route.page}.jsx`
    const mod = pageModules[key]
    // Fail the BUILD, loudly, rather than prerender a route as `undefined` and
    // ship a blank page. A typo'd `page` is otherwise invisible: the client
    // would 404 its chunk at runtime and the prerender would throw something
    // unreadable from deep inside renderToString.
    if (!mod?.default) {
      throw new Error(
        `routes.jsx: route ${route.path} names page "${route.page}", but ` +
        `src/pages/${route.page}.jsx has no default export (or does not exist).`,
      )
    }
    return { ...route, Component: mod.default }
  })
}

// The pages the rebuild replaced. Caddy 301s these in production; the client
// redirects too, so a direct hit behaves the same locally and in the SPA, and an
// old deep link from a bookmark or an ad never lands on a blank route.
//
// ⛔ They are NOT in ROUTES, so they are not prerendered and not in the sitemap:
// a redirected URL with a static file sitting on it would be served the file.
export const REDIRECTS = [
  { from: '/for-workers',   to: '/' },
  { from: '/get-paid',      to: '/' },
  { from: '/waitlist',      to: '/' },
  { from: '/for-practices', to: '/business' },
]

// The derived lists the build scripts read. Plain data, so a node script can
// import them straight out of the SSR bundle without touching JSX.
//
// `page` rides along on PRERENDER_ROUTES so prerender.js can look the route's
// chunk up in Vite's build manifest and emit a modulepreload for it. Without
// that the browser only discovers the page chunk after the entry chunk has
// parsed and run, costing a round trip before the page becomes interactive.
export const PRERENDER_ROUTES = ROUTE_TABLE
  .filter((r) => r.prerender)
  .flatMap((r) =>
    (r.prerenderPaths ?? [{ url: r.path, out: r.out }]).map((p) => ({ ...p, page: r.page })))

export const SITEMAP_ROUTES = ROUTE_TABLE
  .flatMap((r) => r.sitemapPaths ?? (r.sitemap ? [{ path: r.path, ...r.sitemap }] : []))
